import {memo, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import { notifyError } from '../../../../../utils/notifications'
import {createSearchParams, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {Checkbox, Collapse, Empty, Flex, Form, Table, Typography} from 'antd'
import Button from '../../../../common/Button'
import InputText from '../../../../common/InputText'
import NoRecordsFound from '../../../../common/NoRecordsFound'
import {formatEmptyValue} from '../../../../../utils/common'
import useDebouncedValue from '../../../../../utils/common_function'
import {getRequest} from '../../../../../services/base'
import {NavContext} from '../../../../../context/global_context'
import {SET_CANDIDATE_CONNECTORS, SET_CIRCUIT_PINS, useSearchContext,} from '../context/context'
import EllipsisTooltip from '../../../../common/EllipsisTooltip'
import RecordsCount from '../../../../common/RecordsCount'
import useTableScrollY, { tableAreaStyle, emptyStateStyle } from '../useTableScrollY'

const buildPinKey = (circuitId, pinFrom, connectorFrom) =>
  `${circuitId}**$$${pinFrom}**$$${connectorFrom}`

const highlightText = (text, term) => {
  const value = String(formatEmptyValue(text))
  if (!term) return value
  const idx = value.toLowerCase().indexOf(term.toLowerCase())
  if (idx === -1) return value
  return (
    <>
      {value.slice(0, idx)}
      <mark className="search-highlight">
        {value.slice(idx, idx + term.length)}
      </mark>
      {value.slice(idx + term.length)}
    </>
  )
}

const CircuitPinsTable = () => {
  const intl = useIntl()
  const [state, dispatch] = useSearchContext()
  const [, navDispatch] = useContext(NavContext)
  const { vehicle } = useParams()
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, 500)
  const [activeKey, setActiveKey] = useState([])
  const [tableAreaRef, scrollY] = useTableScrollY([state.selectedRecord])

  const isDevice = state.selectedRecord?.type === 'devices'

  const filterRows = useCallback((rows, search) => {
    if (!search) return rows
    return rows.filter((row) =>
      `${row.circuit_name} ${row.circuit_desc} ${row.pin_from}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )
  }, [])

  const filteredData = useMemo(
    () => filterRows(state.selectedRecord?.data || [], debouncedSearch),
    [state.selectedRecord?.data, debouncedSearch, filterRows]
  )

  const groupedData = useMemo(() => {
    if (!isDevice) return null
    const groups = {}
    filteredData.forEach((row) => {
      let connector = row.connector_from
      if (row.connector_to?.startsWith(state.selectedRecord?.name)) {
        connector = row.connector_to
      }
      if (!groups[connector]) groups[connector] = []
      groups[connector].push(row)
    })
    return groups
  }, [filteredData, isDevice, state.selectedRecord?.name])

  // Reset / auto-open groups when the record or the search term changes.
  // New search term -> expand every matching group.
  // New record (browse) -> open the first connector by default.
  useEffect(() => {
    const keys = Object.keys(groupedData || {})
    setActiveKey(debouncedSearch ? keys : keys[0] ? [keys[0]] : [])
  }, [state.selectedRecord, debouncedSearch, groupedData])

  const currentGroupKeys = Object.keys(groupedData || {})

  // Respect user toggles in both modes (all-collapsed is allowed).
  // Browse mode keeps a single panel open (accordion); search allows many.
  const validKeys = activeKey.filter((key) => currentGroupKeys.includes(key))
  const expandedKeys = debouncedSearch ? validKeys : validKeys.slice(0, 1)

  const handleCollapseChange = useCallback((keys) => {
    const next = Array.isArray(keys) ? keys : keys ? [keys] : []
    setActiveKey(next)
  }, [])

  const toggleCircuitPin = useCallback(
    (row, connectorOverride) => {
      dispatch({
        type: SET_CIRCUIT_PINS,
        data: buildPinKey(row.circuit_id, row.pin_from, connectorOverride),
        startConnectors: connectorOverride,
      })
    },
    [dispatch]
  )

  const columns = useMemo(
    () => (connectorOverride) => [
      {
        title: '',
        width: 30,
        render: (_, row) => (
          <Checkbox
            checked={state.selectedCircuitPins.includes(
              buildPinKey(row.circuit_id, row.pin_from, connectorOverride)
            )}
            onChange={() => toggleCircuitPin(row, connectorOverride)}
          />
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'functionalDiagram.circuit' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'circuit_name',
        sorter: (a, b) =>
          (a.circuit_name || '').localeCompare(b.circuit_name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {highlightText(text, debouncedSearch)}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'generic.pin' })}
          </EllipsisTooltip>
        ),
        width: 45,
        dataIndex: 'pin_from',
        render: (text) => highlightText(text, debouncedSearch),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'generic.color' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'color',
        width: 75,
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'generic.description' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'circuit_desc',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {highlightText(text, debouncedSearch)}
          </EllipsisTooltip>
        ),
      },
    ],
    [state.selectedCircuitPins, toggleCircuitPin, debouncedSearch, intl]
  )

  const filterConnectors = useCallback(async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'functionalDiagram.fetchingRelatedConnectors',
        }),
        showLoader: true,
      },
      notification: {},
    })

    let circuit_ids = [],
      pins = [],
      connector_names = []
    state.selectedCircuitPins.forEach((row) => {
      const [circuit_id, pin_from, connector_from] = row.split('**$$')
      circuit_ids.push(circuit_id)
      pins.push(pin_from)
      connector_names.push(connector_from)
    })

    let res = await getRequest(
      `/api/dtcs/related_connectors/?${createSearchParams({
        circuit_ids: circuit_ids,
        search_old_levels: true,
        dtc_mapping_type: 'end',
        vehicle_id: vehicle,
        connector_name: connector_names,
        pins: pins,
        is_dtc: false,
      })}`
    )
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.candidateConnectorsError' }))
      return
    }
    dispatch({ type: SET_CANDIDATE_CONNECTORS, data: res.data })
    navDispatch({ type: 'loader', data: {}, notification: {} })
  }, [state.selectedCircuitPins, vehicle, dispatch, navDispatch, intl])

  const connectorColumns = useMemo(
    () =>
      columns(undefined).map((col) =>
        col.title === ''
          ? {
              ...col,
              render: (_, row) => (
                <Checkbox
                  checked={state.selectedCircuitPins.includes(
                    buildPinKey(
                      row.circuit_id,
                      row.pin_from,
                      row.connector_from
                    )
                  )}
                  onChange={() => toggleCircuitPin(row, row.connector_from)}
                />
              ),
            }
          : col
      ),
    [columns, state.selectedCircuitPins, toggleCircuitPin]
  )

  return (
    <Flex vertical gap={16} style={{ flex: 1, minHeight: 0 }}>
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="center" gap={8}>
          <Typography.Text strong>
            {intl.formatMessage({ id: 'functionalDiagram.selectCircuit' })}
          </Typography.Text>
          {isDevice && (
            <Typography.Text type="secondary" className="text-xs">
              {intl.formatMessage(
                { id: 'functionalDiagram.connectorSummary' },
                { count: currentGroupKeys.length }
              )}
              {' · '}
              {intl.formatMessage(
                { id: 'functionalDiagram.pinSummary' },
                { count: filteredData?.length || 0 }
              )}
            </Typography.Text>
          )}
        </Flex>
        <Form>
          <InputText
            type="search"
            name="search_circuit"
            placeholder={intl.formatMessage({ id: 'forms.search' })}
            isRequired={false}
            value={searchValue}
            onChange={setSearchValue}
          />
        </Form>
      </Flex>

      {isDevice ? (
        <>
          <Flex
            vertical
            gap={8}
            className="device-group-list"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            {currentGroupKeys.length > 0 ? (
              <Collapse
                accordion={!debouncedSearch}
                destroyOnHidden
                activeKey={expandedKeys}
                onChange={handleCollapseChange}
                items={currentGroupKeys.map((connector) => ({
                  key: connector,
                  label: (
                    <Typography.Text className="device-group-header bold">
                      {connector} ({groupedData[connector].length})
                    </Typography.Text>
                  ),
                  children: (
                    <Table
                      rowKey={(row) =>
                        buildPinKey(row.circuit_id, row.pin_from, connector)
                      }
                      columns={columns(connector)}
                      dataSource={groupedData[connector]}
                      pagination={false}
                      showSorterTooltip={false}
                      tableLayout='fixed'
                      locale={{ emptyText: <NoRecordsFound /> }}
                    />
                  ),
                }))}
              />
            ) : (
              <NoRecordsFound />
            )}
          </Flex>
        </>
      ) : (
        <div
          ref={tableAreaRef}
          className='circuit-list'
          style={tableAreaStyle}
          data-testid="fd-circuit-pins"
        >
          <Table
            rowKey={(row) =>
              buildPinKey(row.circuit_id, row.pin_from, row.connector_from)
            }
            columns={connectorColumns}
            dataSource={filteredData}
            pagination={false}
            showSorterTooltip={false}
            scroll={{ y: scrollY > 0 ? scrollY : undefined }}
            locale={{
              emptyText: (
                <div style={emptyStateStyle(scrollY)}>
                  <Empty description={<RecordsCount count={0} />} />
                </div>
              ),
            }}
          />
        </div>
      )}

      {filteredData?.length > 0 && (
        <Button
          type="primary"
          disabled={state.selectedCircuitPins?.length > 0 ? false : true}
          onClick={filterConnectors}
          testId="fd-filter"
        >
          {intl.formatMessage({ id: 'functionalDiagram.filter' })}
        </Button>
      )}
    </Flex>
  )
}

export default memo(CircuitPinsTable)
